import UploadDocumentsIcon from '@components/Icons/UploadDocumentsIcon';
import { UploadButton } from '@components/MediaUploadBox/UploadButton';
import { FOLDER_ROOT_PATH } from '@lib/library/folderPath';
import { ActionButton } from '@pages/ActionButton';
import { FolderGlyph } from '@pages/Library/components/Folders/FolderGlyph';
import { useAddVideosModal } from '@pages/Library/context/AddVideosModalContext';
import { LibraryStore } from '@stores/Library/Library';
import { useLibraryFolder } from '@stores/Library/LibraryFolder';
import { observer } from 'mobx-react';
import css from './EmptyLibrary.module.scss';

export const EmptyLibrary = ({ library }: { library: LibraryStore }) => {
  const addVideosModal = useAddVideosModal();
  return (
    <div className={css.emptyLibrary}>
      <UploadDocumentsIcon />
      <span className={css.text}>
        <h1>No media yet</h1>
        <span>
          This library doesn't have any media.
          <br />
          Add some to start exploring.
        </span>
      </span>
      <UploadButton
        variant="primary"
        text="Add media"
        destination={{ libraryId: library.libraryId }}
        onRegisterClose={
          addVideosModal ? (getClose) => addVideosModal.registerClose(getClose) : undefined
        }
      />
    </div>
  );
};

export const NoResultsForFilter = ({ clearFilter }: { clearFilter: () => void }) => {
  return (
    <div className={css.emptyLibrary}>
      <UploadDocumentsIcon />
      <span className={css.text}>
        <h1>No results found</h1>
      </span>
      <ActionButton variant="primary" onClick={clearFilter}>
        Clear all filters
      </ActionButton>
    </div>
  );
};

/**
 * A folder with no media of its own — or the root of a library whose media are all filed in
 * folders. Distinct from `EmptyLibrary`: the library is not empty, so "No media yet" would be
 * false, and adding media here files it in this folder.
 */
export const EmptyFolder = observer(({ library }: { library: LibraryStore }) => {
  const folder = useLibraryFolder();
  const addVideosModal = useAddVideosModal();
  const atRoot = folder.currentFolderPath === FOLDER_ROOT_PATH;

  // The wrapped folders above are the content, and "Add media" is already on the search line.
  if (folder.holdsOnlyFolders) return null;

  const hasSubfolders = folder.childFolders.length > 0;
  const color = folder.foldersByPath.get(folder.currentFolderPath)?.color;
  const [title, description] = atRoot
    ? ['Everything is in folders', 'Open a folder above to see its media.']
    : hasSubfolders
      ? [
          'No media here yet',
          'Open a folder above, or drop files here to upload them into this one.',
        ]
      : [
          'This folder is empty',
          'Drop files here to upload them into it, or use “Move to…” on any media.',
        ];

  return (
    <div className={css.emptyLibrary}>
      <FolderGlyph color={color} open size={56} />
      <span className={css.text}>
        <h1>{title}</h1>
        <span>{description}</span>
      </span>
      {!library.isReadOnly && (
        <UploadButton
          variant="primary"
          text="Add media"
          destination={{ libraryId: library.libraryId, folderPath: folder.currentFolderPath }}
          onRegisterClose={
            addVideosModal ? (getClose) => addVideosModal.registerClose(getClose) : undefined
          }
        />
      )}
    </div>
  );
});
